import "admin/src/style/videoMetadataPage.css";
import { _ } from "i18n";
import client from "libs/client";
import { Link, useLocation, useNavigate } from "react-router";
import { useToast } from "ui-kit";
import { getVideoUploadErrorMessage } from "./videoUploadErrors";

interface ApprovalModalProps {
	videoId: number;
	approved: boolean;
	selectedGame?: {
		game: number | undefined;
		withVideo: boolean;
	};
	close: () => void;
}

export function ApprovalModal({ videoId, approved, selectedGame, close }: ApprovalModalProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const navigate = useNavigate();
	const location = useLocation();

	const approveMutation = client.approveVideoMetadata.useMutation({
		onSuccess: () => {
			addSuccessToast(_("message.metadata_approval.success"));
			close();
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("metadataVideos"),
			});
			if (location.pathname !== "/video-uploads/in-queue") {
				navigate("/video-uploads/in-queue")
			}
		},
		// The API now returns the video service status + a `{ message }` body (e.g. metadata incomplete);
		// unwrap it so the admin sees why approval failed instead of a raw payload / generic 500.
		onError: (res) => addErrorToast(getVideoUploadErrorMessage(res)),
	});

	const revokeApprovalMutation = client.removeApproveVideoMetadata.useMutation({
		onSuccess: () => {
			addSuccessToast(_("message.metadata_approval.success"));
			close();
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("metadataVideos"),
			});
		},
		onError: (res) => addErrorToast(getVideoUploadErrorMessage(res)),
	});

	const onSubmit = () => {
		if (approved) {
			revokeApprovalMutation.mutate({ id: videoId });
		} else {
			approveMutation.mutate({ id: videoId });
		}
	};

	return (
		<div className="approveVideoModal-container">
			<div className="approveMessage">
				{approved ?
					<>{_("admin.videoUploads.metadataEditor.modalRevokeText")}</>
					:
					<>
						<div>{_("admin.videoUploads.metadataEditor.modalApproveText")}</div>
						{selectedGame?.withVideo && <Link to={`/admin/videos/match/${selectedGame.game}`} target="_blank" className="withVideoWarning">{_("admin.video.selectedGameWithVideoWarning")}</Link >}
					</>}
			</div>
			<div className="buttons">
				<div className="submit" onClick={() => onSubmit()}>
					{_("admin.videoUploads.metadataEditor.modalButtonSubmit")}
				</div>
				<div className="cancel" onClick={() => close()}>
					{_("videos.modal.add_match.cancel")}
				</div>
			</div>
		</div>
	);
}
